import { useState, useCallback, useRef, useEffect } from "react";
import type {
  SearchOptions,
  NavigateDirection,
} from "../ui/components/global/_findBar";

interface UseFindBarOptions {
  onSearch?: (searchTerm: string, options: SearchOptions) => number;
  onNavigate?: (direction: NavigateDirection, currentIndex: number) => void;
  autoManage?: boolean;
}

interface UseFindBarReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  searchTerm: string;
  totalResults: number;
  currentResult: number;
  searchOptions: SearchOptions;
  handleSearch: (term: string, options: SearchOptions) => void;
  handleNavigate: (direction: NavigateDirection) => void;
}

export const useFindBar = ({
  onSearch,
  onNavigate,
  autoManage = true,
}: UseFindBarOptions = {}): UseFindBarReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalResults, setTotalResults] = useState(0);
  const [currentResult, setCurrentResult] = useState(0);
  const [searchOptions, setSearchOptions] = useState<SearchOptions>({
    caseSensitive: false,
    wholeWord: false,
  });

  const currentIndexRef = useRef(0);
  const resultsRef = useRef<number[]>([]);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearchTerm("");
    setTotalResults(0);
    setCurrentResult(0);
    currentIndexRef.current = 0;
    resultsRef.current = [];
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleSearch = useCallback(
    (term: string, options: SearchOptions) => {
      setSearchTerm(term);
      setSearchOptions(options);

      if (!term.trim()) {
        setTotalResults(0);
        setCurrentResult(0);
        currentIndexRef.current = 0;
        resultsRef.current = [];
        return;
      }

      const count = onSearch?.(term, options) ?? 0;
      setTotalResults(count);

      if (count > 0) {
        setCurrentResult(1);
        currentIndexRef.current = 0;
      } else {
        setCurrentResult(0);
        currentIndexRef.current = -1;
      }
    },
    [onSearch]
  );

  const handleNavigate = useCallback(
    (direction: NavigateDirection) => {
      if (totalResults === 0) return;

      let newIndex = currentIndexRef.current;

      if (direction === "next") {
        newIndex = (newIndex + 1) % totalResults;
      } else {
        newIndex = newIndex === 0 ? totalResults - 1 : newIndex - 1;
      }

      currentIndexRef.current = newIndex;
      setCurrentResult(newIndex + 1);
      onNavigate?.(direction, newIndex);
    },
    [totalResults, onNavigate]
  );

  useEffect(() => {
    if (!autoManage) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        open();
      }

      if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        close();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [autoManage, isOpen, open, close]);

  return {
    isOpen,
    open,
    close,
    toggle,
    searchTerm,
    totalResults,
    currentResult,
    searchOptions,
    handleSearch,
    handleNavigate,
  };
};
